/**
 * Print a cloned CV DOM node to the system print dialog.
 *
 * Uses an off-screen iframe on desktop (iframe.contentWindow.print) and a
 * dedicated popup window on iOS (Safari refuses to print off-screen iframes).
 * Waits for stylesheets to load before triggering print and has a 2.5 s safety
 * fallback. Always restores the document title and calls onDone() when the
 * print dialog closes (afterprint event or 5 s cleanup timeout).
 */

export interface PrintCvOptions {
  /** Title shown in the print dialog and saved PDF filename. */
  title: string;
  /** Called when the print session has fully torn down (iframe removed / popup closed). */
  onDone?: () => void;
}

const CLEANUP_MS = 5000;
const STYLE_WAIT_FALLBACK_MS = 2500;
const PRINT_DELAY_MS = 80;

const PRINT_CSS =
  '<style>@media print{' +
  '@page{size:A4;margin:12mm;}' +
  'html,body{height:auto!important;min-height:0!important;overflow:visible!important;margin:0;padding:0;}' +
  'body::after{display:none!important;content:none!important;}' +
  '.cv-container{page-break-after:avoid!important;}' +
  '}</style>';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function collectStylesheetLinks(): string {
  const hrefs = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    .map((l) => l.href)
    .filter(Boolean);
  return hrefs.map((h) => `<link rel="stylesheet" href="${h.replace(/"/g, '&quot;')}">`).join('');
}

function buildDocHtml(title: string): string {
  const safeTitle = escapeHtml(title);
  const linkTags = collectStylesheetLinks();
  return (
    `<!DOCTYPE html><html class="print-wysiwyg"><head>` +
    `<meta charset="utf-8"><meta name="viewport" content="width=794">` +
    `<title>${safeTitle}</title>${PRINT_CSS}${linkTags}` +
    `</head>` +
    `<body style="margin:0;padding:0;background:#fff;width:794px;min-width:794px;height:auto;min-height:0;box-sizing:border-box;"></body></html>`
  );
}

function waitForStylesheetsThen(doc: Document, run: () => void, fallbackMs = STYLE_WAIT_FALLBACK_MS) {
  const styleLinks = doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]');
  if (styleLinks.length === 0) {
    setTimeout(run, PRINT_DELAY_MS);
    return;
  }
  let loaded = 0;
  const onLoad = () => {
    loaded++;
    if (loaded >= styleLinks.length) setTimeout(run, PRINT_DELAY_MS);
  };
  styleLinks.forEach((link) => {
    link.onload = onLoad;
    link.onerror = onLoad;
  });
  // Fallback in case a link never fires (cache, blocker, etc.)
  setTimeout(run, fallbackMs);
}

export function printCvNode(
  node: HTMLElement,
  { title, onDone }: PrintCvOptions,
): void {
  const originalTitle = document.title;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);

  const finish = (restore: () => void) => {
    restore();
    onDone?.();
  };

  // ---- iOS path : dedicated popup opened in the same gesture ----
  if (isIOS) {
    const printWin = window.open('', '_blank');
    if (!printWin) {
      onDone?.();
      return;
    }
    document.title = title;
    setTimeout(() => {
      const doc = printWin.document;
      doc.open();
      doc.write(buildDocHtml(title));
      doc.close();
      doc.body.appendChild(node.cloneNode(true) as HTMLDivElement);

      const restore = () => {
        document.title = originalTitle;
        try {
          printWin.close();
        } catch {
          /* ignore */
        }
      };

      waitForStylesheetsThen(doc, () => {
        try {
          printWin.print();
          printWin.addEventListener('afterprint', () => finish(restore), { once: true });
        } catch {
          finish(restore);
        }
        setTimeout(() => finish(restore), CLEANUP_MS);
      });
    }, 500);
    return;
  }

  // ---- Desktop path : off-screen iframe ----
  setTimeout(() => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:-9999px;top:0;width:794px;min-height:1123px;height:5000px;border:0;z-index:99999;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      finish(() => {
        document.title = originalTitle;
      });
      return;
    }

    document.title = title;
    doc.open();
    doc.write(buildDocHtml(title));
    doc.close();
    doc.body.appendChild(node.cloneNode(true) as HTMLDivElement);

    let didPrint = false;
    let fallbackId: ReturnType<typeof setTimeout> | null = null;
    const restore = () => {
      document.title = originalTitle;
      iframe.remove();
    };
    const doCleanup = () => {
      if (fallbackId != null) clearTimeout(fallbackId);
      fallbackId = null;
      finish(restore);
    };
    const runPrint = () => {
      if (didPrint) return;
      didPrint = true;
      if (fallbackId != null) clearTimeout(fallbackId);
      fallbackId = null;
      try {
        iframe.contentWindow!.print();
        iframe.contentWindow!.addEventListener('afterprint', () => doCleanup(), { once: true });
      } catch {
        doCleanup();
      }
      setTimeout(doCleanup, CLEANUP_MS);
    };

    waitForStylesheetsThen(doc, runPrint);
  }, 0);
}
